/* eslint-disable import/no-anonymous-default-export */
import Icon from "@material-ui/icons/ShoppingBasket";
import { FC } from "react";
import {
  BooleanField,
  BooleanInput,
  Create,
  CreateProps,
  Datagrid,
  Edit,
  EditProps,
  Filter,
  FunctionField,
  List,
  ListProps,
  SaveButton,
  SelectInput,
  Show,
  ShowProps,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
} from "react-admin";
import CrudBreadcrumb from "../components/CrudBreadcrumb";
import StatusField from "../components/StatusField";
import BusinessUnitInput from "../components/inputs/BusinessUnitInput";
import ProductTypeInput from "../components/inputs/ProductTypeInput";

const isSR = process.env.REACT_APP_COMPANY_CODE === "SNR";

const productTypeChoices = isSR
  ? [
      { id: "instrument", name: "Device" },
      { id: "implant", name: "Consumable" },
      { id: "others", name: "Others" },
    ]
  : [
      { id: "instrument", name: "Instrument" },
      { id: "implant", name: "Implant" },
      { id: "others", name: "Others" },
    ];

const productTypeMap = productTypeChoices.reduce<Record<string, string>>(
  (acc, choice) => {
    acc[choice.id] = choice.name;
    return acc;
  },
  {},
);

const ResourceList: FC<ListProps> = (props) => (
  <List
    {...props}
    exporter={false}
    sort={{ field: "id", order: "desc" }}
    filters={<ListFilter />}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show">
      <TextField source="id" />
      <TextField source="name" />
      <TextField source="business_unit.name" label="Business Unit" />
      <FunctionField
        label="Type"
        render={(record: any) => productTypeMap[record?.type] ?? record?.type}
      />
      <BooleanField source="is_one_time_use" />
      <StatusField source="status" />
    </Datagrid>
  </List>
);

const ListFilter: FC<any> = (props) => (
  <Filter {...props}>
    <TextInput source="id" label="ID" />
    <TextInput source="search" label="Name and Description" />
    <SelectInput source="type" choices={productTypeChoices} />
    <BusinessUnitInput source="business_unit_id" />
  </Filter>
);

const ResourceShow: FC<ShowProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={props.id}
        mode="show"
      />
      <Show {...props}>
        <SimpleShowLayout>
          <TextField source="id" />
          <TextField source="name" />
          <TextField source="business_unit.name" label="Business Unit" />
          <FunctionField
            label="Type"
            render={(record: any) =>
              productTypeMap[record?.type] ?? record?.type
            }
          />
          <TextField source="description" />
          <BooleanField source="is_one_time_use" />
          <StatusField source="status" />
        </SimpleShowLayout>
      </Show>
    </>
  );
};

const ResourceEdit: FC<EditProps> = (props) => {
  const IssueEditToolbar: FC<any> = (props) => (
    <Toolbar {...props}>
      <SaveButton disabled={props.pristine} />
    </Toolbar>
  );
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={props.id}
        mode="edit"
      />
      <Edit {...props}>
        <SimpleForm redirect="show" toolbar={<IssueEditToolbar />}>
          <TextField source="id" />
          <TextInput source="name" />
          <BusinessUnitInput source="business_unit_id" />
          <ProductTypeInput source="type" />
          <TextInput source="description" />
          <BooleanInput source="is_one_time_use" label="One Time Use" />
          <SelectInput
            source="status"
            choices={[
              { id: "active", name: "Active" },
              { id: "inactive", name: "Inactive" },
            ]}
          />
        </SimpleForm>
      </Edit>
    </>
  );
};

const ResourceCreate: FC<CreateProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={""}
        mode="create"
      />
      <Create {...props}>
        <SimpleForm>
          <TextInput source="name" />
          <BusinessUnitInput source="business_unit_id" />
          <SelectInput source="type" choices={productTypeChoices} />
          <TextInput source="description" />
          <BooleanInput source="is_one_time_use" label="One Time Use" />
        </SimpleForm>
      </Create>
    </>
  );
};

const Common = {
  resourceName: "products",
};

export default {
  name: Common.resourceName,
  icon: Icon,
  list: ResourceList,
  show: ResourceShow,
  create: ResourceCreate,
  edit: ResourceEdit,
};
